import type React from "react";
import {useEffect, useLayoutEffect, useState} from "react";
import {Group, Panel, Separator, useGroupRef, usePanelRef, type GroupImperativeHandle, type PanelImperativeHandle} from "react-resizable-panels";
import {useLocation, useNavigate} from "react-router";
import {useChatContext} from "./context/ChatContext";
import {ChatArea} from "./components/ChatArea/ChatArea";
import {SupersetDashboardPanel} from "./components/SupersetDashboardPanel/SupersetDashboardPanel";

const SPLIT_LAYOUT = {"chat-panel": 45, "dashboard-panel": 55} as const;
/** getLayout() / asPercentage are 0–100 (45 = 45%), not 0–1. */
const COLLAPSED_PERCENT_MAX = 1;

function isChatPanelCollapsed(group: GroupImperativeHandle | null, chatPanel: PanelImperativeHandle | null) {
    if (chatPanel?.isCollapsed()) return true;
    const chatPercent = group?.getLayout()["chat-panel"];
    return chatPercent !== undefined && chatPercent < COLLAPSED_PERCENT_MAX;
}

/** Router navigation state accepted by ChatPage (set by the Dashboards page). */
export type ChatPageLocationState = {
    /** Open the session's embedded dashboard in fullscreen once it restores. */
    openDashboardFullscreen?: boolean;
    /** When set, only that dashboard triggers fullscreen (guards against a stale panel from a previous session). */
    dashboardUuid?: string | null;
} | null;

export const ChatPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {clearSessionLoading, embeddedDashboard} = useChatContext();
    const [isChatCollapsed, setIsChatCollapsed] = useState(false);
    const groupRef = useGroupRef();
    const chatPanelRef = usePanelRef();
    const dashboardsState = location.state as ChatPageLocationState;
    const shouldOpenFromDashboards = Boolean(
        embeddedDashboard &&
        dashboardsState?.openDashboardFullscreen &&
        (!dashboardsState.dashboardUuid || dashboardsState.dashboardUuid === embeddedDashboard.uuid),
    );

    // Chat cannot stay collapsed after the dashboard is gone; Dashboards-page
    // arrivals collapse chat once the panel is on screen.
    if (!embeddedDashboard && isChatCollapsed) {
        setIsChatCollapsed(false);
    }

    // Clear the Dashboards navigation flag so later dashboard updates (or exiting fullscreen)
    // are not overridden.
    useEffect(() => {
        if (!shouldOpenFromDashboards) return;
        navigate({pathname: location.pathname, search: location.search}, {replace: true});
    }, [shouldOpenFromDashboards, location.pathname, location.search, navigate]);

    // ChatProvider outlives this page — clear restore/switch loading on unmount.
    useEffect(() => {
        return () => {
            clearSessionLoading();
        };
    }, [clearSessionLoading]);

    useLayoutEffect(() => {
        if (!shouldOpenFromDashboards) return;
        chatPanelRef.current?.collapse();
    }, [chatPanelRef, shouldOpenFromDashboards]);

    const onToggleFullscreen = () => {
        const group = groupRef.current;
        const chatPanel = chatPanelRef.current;
        if (!group || !chatPanel || Object.keys(group.getLayout()).length !== 2) return;

        if (isChatPanelCollapsed(group, chatPanel)) {
            group.setLayout(SPLIT_LAYOUT);
        } else {
            chatPanel.collapse();
        }
    };

    return (
        <Group
            id="chat-dashboard-split"
            orientation="horizontal"
            className="chat-split-group"
            groupRef={groupRef}
        >
            {/* Always mounted so input draft and scroll survive dashboard fullscreen. */}
            <Panel
                id="chat-panel"
                panelRef={chatPanelRef}
                collapsible={Boolean(embeddedDashboard)}
                collapsedSize={0}
                minSize={embeddedDashboard ? 530 : undefined}
                defaultSize={embeddedDashboard ? "45%" : undefined}
                className="chat-split-panel"
                onResize={(size) => {
                    const collapsed = size.asPercentage < COLLAPSED_PERCENT_MAX;
                    setIsChatCollapsed(prev => (prev === collapsed ? prev : collapsed));
                }}
            >
                <ChatArea/>
            </Panel>
            {embeddedDashboard && (
                <Separator
                    id="chat-dashboard-separator"
                    className="chat-split-separator"
                />
            )}
            {embeddedDashboard && (
                <Panel
                    id="dashboard-panel"
                    minSize={420}
                    defaultSize="55%"
                    className="chat-split-panel"
                >
                    <SupersetDashboardPanel
                        // Keyed by uuid + refreshToken: remounts (reloading the iframe) when the
                        // agent switches dashboards OR edits the current one (same embed uuid).
                        key={`${embeddedDashboard.uuid}:${embeddedDashboard.refreshToken}`}
                        dashboard={embeddedDashboard}
                        isFullscreen={isChatCollapsed}
                        onToggleFullscreen={onToggleFullscreen}
                    />
                </Panel>
            )}
        </Group>
    );
};
